import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accessibility,
  Baby,
  Briefcase,
  ChevronRight,
  Dog,
  Eye,
  Globe2,
  GraduationCap,
  HeartHandshake,
  Home,
  Languages,
  Milk,
  Minus,
  Plus,
  Save,
  Smile,
  Sparkles,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/config';
import { useAlert } from '../contexts/AlertContext';
import { apiService } from '../services/api';
import { DATA_DICTIONARY_TYPES, useDataDictionaries } from '../hooks/useDataDictionary';
import { CookingSkill, CreateJobPostingRequest, DataDictionary, JobPosting } from '../types';
import { getRestDayOptions } from '../constants/formOptions';
import { getDictDisplayValue } from '../utils/i18n';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

type ActiveSection = 'headline' | 'overview' | 'household' | 'requirements' | 'preferences';

type Option = {
  value: string;
  label: string;
};

type FormFieldName =
  | 'title'
  | 'headline'
  | 'location'
  | 'workType'
  | 'salaryType'
  | 'salaryCurrency'
  | 'salaryOtherOption'
  | 'contractSituation'
  | 'livingArrangement'
  | 'requiredEducationLevel'
  | 'preferredGender'
  | 'preferredNationality'
  | 'preferredMaritalStatus'
  | 'preferredHasChildren'
  | 'preferredReligion'
  | 'helperOriginPreference'
  | 'restDayOption';

const optionalNumberSchema = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
  z.number().finite().nonnegative().optional(),
);

const familyNumberSchema = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? 0 : Number(value)),
  z.number().finite().nonnegative(),
);

// 年齡上下限對齊後端 CreateJobPostingRequest（18-60 歲）
const getAgeRangeSchema = (t: any) => z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
  z.number().finite().min(18, t('jobForm.valAgeRange', '年齡必須介乎 18 至 60 歲')).max(60, t('jobForm.valAgeRange', '年齡必須介乎 18 至 60 歲')).optional(),
);

// 經驗年數上限對齊後端 CreateJobPostingRequest（0-40 年）
const getExperienceYearsSchema = (t: any) => z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
  z.number().finite().min(0).max(40, t('jobForm.valExperienceMax', '經驗年數不可超過 40 年')).optional(),
);

const getJobPostingSchema = (t: any) => z
  .object({
    employerId: familyNumberSchema,
    title: z.string().trim().min(1, t('jobPosting.titleRequired', '請選擇職位名稱')),
    headline: z.string().trim().min(1, t('jobForm.valHeadlineRequired', '請填寫廣告標題')),
    description: z.string().trim().min(1, t('jobPosting.descriptionRequired', 'Job description is required')),
    location: z.string().trim().min(1, t('jobPosting.locationRequired', 'Location is required')),
    requiredSkills: z.array(z.string()).default([]),
    requiredLanguages: z.array(z.string()).min(1, t('jobForm.valLanguagesRequired', '請至少選擇一項所需語言')),
    spokenLanguages: z.array(z.string()).default([]),
    cookingSkills: z.array(z.string()).default([]),
    workType: z.string().min(1, t('jobPosting.workTypeRequired', 'Work type is required')),
    otherSkills: z.array(z.string()).default([]),
    salaryType: z.string().min(1),
    salaryCurrency: z.string().min(1),
    fixedSalary: optionalNumberSchema,
    minSalary: optionalNumberSchema,
    maxSalary: optionalNumberSchema,
    salaryOtherOption: z.string().optional(),
    minAge: getAgeRangeSchema(t),
    maxAge: getAgeRangeSchema(t),
    minExperienceYears: getExperienceYearsSchema(t),
    maxExperienceYears: getExperienceYearsSchema(t),
    accommodation: z.string().optional(),
    employerNationality: z.string().min(1, t('jobForm.employerNationalityRequired', '請選擇僱主國籍')),
    specialRequirements: z.string().optional(),
    preferredGender: z.string().min(1, t('jobForm.valGenderRequired', '請選擇外傭性別')),
    preferredNationality: z.string().min(1, t('jobForm.valNationalityRequired', '請選擇國籍')),
    restDayOption: z.string().min(1),
    requiredEducationLevel: z.string().min(1),
    preferredMaritalStatus: z.string().min(1, t('jobForm.valMaritalRequired', '請選擇婚姻狀況')),
    preferredHasChildren: z.string().optional(),
    startDateFlexible: z.boolean().default(false),
    requiredStartDate: z.string().regex(/^(\d{4}\/\d{2}(\/\d{2})?)?$/, t('jobForm.valStartDateFormat', '格式須為 YYYY/MM 或 YYYY/MM/DD')).optional(),
    benefits: z.array(z.string()).default([]),
    familyMembers: z.object({
      adults: familyNumberSchema,
      newborn: familyNumberSchema,
      infant: familyNumberSchema,
      children: familyNumberSchema,
      teen: familyNumberSchema,
      elderly: familyNumberSchema,
      pets: familyNumberSchema,
      specialNeeds: z.string().optional(),
    }),
    livingArrangement: z.string().min(1, t('jobPosting.livingArrangementRequired', 'Living arrangement is required')),
    numberOfRooms: z.preprocess(
      (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
      z.number().finite().nonnegative().optional(),
    ),
    flatSize: z.preprocess(
      (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
      z.number().finite().positive({ message: t('jobForm.valUnitSize', '請輸入房屋面積') }),
    ),
    preferredReligion: z.string().min(1, t('jobForm.valReligionRequired', '請選擇宗教')),
    helperOriginPreference: z.string().min(1, t('jobForm.valOriginRequired', '請選擇偏好類別')),
    contractSituation: z.string().min(1),
  })
  .superRefine((values, ctx) => {
    if (values.salaryType === 'FIXED' && !values.fixedSalary) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fixedSalary'],
        message: t('jobPosting.fixedSalaryRequired', 'Fixed salary is required for fixed salary postings'),
      });
    }

    if (values.salaryType === 'OTHER' && !values.salaryOtherOption?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['salaryOtherOption'],
        message: t('jobPosting.alternativeSalaryRequired', 'Please choose an alternative salary option'),
      });
    }

    if (values.salaryType === 'RANGE') {
      if (!values.minSalary || !values.maxSalary) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['maxSalary'],
          message: t('jobForm.valSalaryRangeRequired', '請輸入薪金範圍的最低及最高金額'),
        });
      } else if (values.maxSalary <= values.minSalary) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['maxSalary'],
          message: t('jobForm.valSalaryRangeOrder', '最高薪金須大於最低薪金'),
        });
      }
    }

    if (values.minAge !== undefined && values.maxAge !== undefined && values.minAge > values.maxAge) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maxAge'],
        message: t('jobPosting.ageRangeInvalid', 'Maximum age must be greater than or equal to minimum age'),
      });
    }

    if (
      values.minExperienceYears !== undefined &&
      values.maxExperienceYears !== undefined &&
      values.minExperienceYears > values.maxExperienceYears
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maxExperienceYears'],
        message: t('jobPosting.experienceRangeInvalid', 'Maximum experience must be greater than or equal to minimum experience'),
      });
    }

    if (!values.startDateFlexible) {
      if (!values.requiredStartDate?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['requiredStartDate'], message: t('jobForm.valStartDateOrFlexible', '請填寫上班日期或勾選「隨時」') });
      } else {
        const parts = values.requiredStartDate.split('/');
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parts[2] ? parseInt(parts[2]) : 1);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        if (d < today) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['requiredStartDate'], message: t('jobForm.valFutureDate', '日期必須為未來日期') });
      }
    }
  });

type JobPostingFormValues = z.input<ReturnType<typeof getJobPostingSchema>>;
type JobPostingSubmitValues = z.output<ReturnType<typeof getJobPostingSchema>>;

// 選項標籤要跟隨介面語言，改為接收 t 的 factory，於元件內以 useMemo 實例化
type TFn = (key: string, defaultValue: string) => string;

const getSectionItems = (t: TFn): { id: ActiveSection; label: string; description: string }[] => [
  { id: 'headline', label: t('jobForm.sectionHeadline', '廣告標題'), description: t('jobForm.sectionHeadlineDesc', '招聘廣告的標題') },
  { id: 'overview', label: t('jobForm.sectionOverview', '職位概覽'), description: t('jobForm.sectionOverviewDesc', '職位定位、薪資與工作描述') },
  { id: 'household', label: t('jobForm.sectionHousehold', '家庭概況'), description: t('jobForm.sectionHouseholdDesc', '家庭成員、住宿與排班') },
  { id: 'preferences', label: t('jobForm.sectionPreferences', '候選人偏好'), description: t('jobForm.sectionPreferencesDesc', '教育程度、合約狀況、檔案偏好及特別備註') },
  { id: 'requirements', label: t('jobForm.sectionRequirements', '語言與技能'), description: t('jobForm.sectionRequirementsDesc', '語言要求、職責及技能') },
];

const getSalaryTypeOptions = (t: TFn): Option[] => [
  { value: 'FIXED', label: t('jobForm.salaryFixed', '固定薪金') },
  { value: 'NOT_SPECIFIED', label: t('jobForm.salaryNotSpecified', '未指定') },
  { value: 'OTHER', label: t('jobForm.salaryOtherArrangement', '其他安排') },
  { value: 'RANGE', label: t('jobForm.salaryRange', '範圍') },
];

// 薪資類型 + 其他選項 合併為單一下拉：固定薪金 / 範圍 / 法定最低工資 / 面議
const getSalaryChoiceOptions = (t: TFn): Option[] => [
  { value: 'OTHER_LEGAL_MINIMUM', label: t('jobForm.salaryLegalMin', '法定最低工資') },
  { value: 'FIXED', label: t('jobForm.salaryFixed', '固定薪金') },
  { value: 'RANGE', label: t('jobForm.salaryRange', '範圍') },
  { value: 'OTHER_TO_BE_DISCUSS', label: t('jobForm.salaryNegotiable', '面議') },
];

const CURRENCY_CODES = ['HKD', 'TWD', 'SGD', 'MOP', 'MYR', 'THB', 'PHP', 'IDR', 'KRW', 'JPY', 'CNY', 'AED', 'SAR', 'QAR', 'KWD'];
const getSalaryCurrencyOptions = (t: TFn): Option[] =>
  CURRENCY_CODES.map(code => ({ value: code, label: t(`jobForm.currency.${code}`, code) }));

const getSalaryOtherOptions = (t: TFn): Option[] => [
  { value: 'LEGAL_MINIMUM', label: t('jobForm.salaryLegalMin', '法定最低工資') },
  { value: 'TO_BE_DISCUSS', label: t('jobForm.salaryNegotiable', '面議') },
];

// ponytail: 香港、中國置頂，其餘按英文字母排序
const EMPLOYER_COUNTRIES = [
  { value: 'HK', zh: '香港', en: 'Hong Kong' },
  { value: 'CN', zh: '中國', en: 'China' },
  { value: 'MO', zh: '澳門', en: 'Macao' },
  { value: 'TW', zh: '台灣', en: 'Taiwan' },
  { value: 'AU', zh: '澳洲', en: 'Australia' },
  { value: 'CA', zh: '加拿大', en: 'Canada' },
  { value: 'FR', zh: '法國', en: 'France' },
  { value: 'DE', zh: '德國', en: 'Germany' },
  { value: 'ID', zh: '印尼', en: 'Indonesia' },
  { value: 'JP', zh: '日本', en: 'Japan' },
  { value: 'KR', zh: '韓國', en: 'South Korea' },
  { value: 'MY', zh: '馬來西亞', en: 'Malaysia' },
  { value: 'NL', zh: '荷蘭', en: 'Netherlands' },
  { value: 'NZ', zh: '紐西蘭', en: 'New Zealand' },
  { value: 'PH', zh: '菲律賓', en: 'Philippines' },
  { value: 'SG', zh: '新加坡', en: 'Singapore' },
  { value: 'CH', zh: '瑞士', en: 'Switzerland' },
  { value: 'TH', zh: '泰國', en: 'Thailand' },
  { value: 'AE', zh: '阿聯酋', en: 'United Arab Emirates' },
  { value: 'GB', zh: '英國', en: 'United Kingdom' },
  { value: 'US', zh: '美國', en: 'United States' },
];

const getPreferenceAnyOptions = (t: TFn): Record<'gender' | 'nationality' | 'maritalStatus' | 'religion', Option> => ({
  gender: { value: 'ANY', label: t('jobForm.any', '不限') },
  nationality: { value: 'ANY', label: t('jobForm.any', '不限') },
  maritalStatus: { value: 'ANY', label: t('jobForm.any', '不限') },
  religion: { value: 'ANY', label: t('jobForm.any', '不限') },
});


const getFamilyMembers = (t: TFn): Array<{ key: keyof JobPostingFormValues['familyMembers']; label: string; icon: LucideIcon }> => [
  { key: 'adults', label: t('helperProfileForm.familyAdults', '成人'), icon: Users },
  { key: 'newborn', label: t('helperProfileForm.familyNewborn', '初生 (0-6個月)'), icon: Baby },
  { key: 'infant', label: t('helperProfileForm.familyInfant', '嬰幼兒 (6個月-3歲)'), icon: Milk },
  { key: 'children', label: t('helperProfileForm.familyChild', '兒童 (3-12歲)'), icon: Smile },
  { key: 'teen', label: t('helperProfileForm.familyTeen', '少年 (12-18歲)'), icon: GraduationCap },
  { key: 'elderly', label: t('helperProfileForm.familyElderly', '長者 (65歲以上)'), icon: Accessibility },
  { key: 'pets', label: t('helperProfileForm.familyPets', '寵物'), icon: Dog },
];

const createDefaultValues = (employerId?: number): JobPostingFormValues => ({
  employerId: employerId ?? 0,
  title: '',
  headline: '',
  description: '',
  location: '',
  requiredSkills: [],
  requiredLanguages: [],
  spokenLanguages: [],
  cookingSkills: [],
  workType: 'FULL_TIME',
  otherSkills: [],
  salaryType: 'FIXED',
  salaryCurrency: 'HKD',
  fixedSalary: undefined,
  minSalary: undefined,
  maxSalary: undefined,
  salaryOtherOption: '',
  minAge: 25,
  maxAge: 45,
  minExperienceYears: 0,
  maxExperienceYears: 40,
  accommodation: 'Live-in',
  employerNationality: '',
  specialRequirements: '',
  preferredGender: 'ANY',
  preferredNationality: 'ANY',
  restDayOption: 'SUNDAY',
  requiredEducationLevel: 'HIGH_SCHOOL',
  preferredMaritalStatus: 'ANY',
  preferredHasChildren: 'ANY',
  startDateFlexible: false,
  requiredStartDate: '',
  benefits: [],
  familyMembers: {
    adults: 0,
    newborn: 0,
    infant: 0,
    children: 0,
    teen: 0,
    elderly: 0,
    pets: 0,
    specialNeeds: '',
  },
  livingArrangement: '',
  numberOfRooms: undefined,
  flatSize: undefined,
  preferredReligion: 'ANY',
  helperOriginPreference: 'ANY',
  contractSituation: 'ABOUT_TO_FINISH',
});

const parseDelimitedString = (value?: string | null): string[] =>
  value
    ?.split(',')
    .map((item) => item.trim())
    .filter(Boolean) ?? [];

const getOptionsFromDictionaries = (
  dictionaries?: DataDictionary[] | null,
  mode: 'code' | 'english' = 'code',
  fallback: Option[] = [],
): Option[] => {
  if (!dictionaries || dictionaries.length === 0) {
    return fallback;
  }

  return dictionaries.map((dictionary) => ({
    value: mode === 'english' ? dictionary.valueEnglish : dictionary.code,
    label: getDictDisplayValue(dictionary),
  }));
};

const getValueFromOptions = (value: string | undefined | null, options: Option[], fallback = '') => {
  if (!value) {
    return fallback;
  }

  const matched = options.find((option) => option.value === value || option.label === value);
  return matched?.value ?? value;
};

const getLabelFromOptions = (value: string | undefined | null, options: Option[]) => {
  if (!value) {
    return '—';
  }

  const matched = options.find((option) => option.value === value || option.label === value);
  return matched?.label ?? value;
};

const formatDelimitedText = (values: string[], options: Option[]) => values.map((value) => getLabelFromOptions(value, options));

const toDelimitedString = (values: string[]) => values.filter(Boolean).join(',');

const mapPostingToFormValues = (
  posting: JobPosting,
  employerId: number,
  locationOptions: Option[],
  livingArrangementOptions: Option[],
): JobPostingFormValues => ({
  employerId: posting.employerId ?? employerId,
  title: posting.title ?? '',
  headline: posting.headline ?? '',
  description: posting.description ?? '',
  location: getValueFromOptions(posting.location, locationOptions),
  requiredSkills: posting.requiredSkills ?? [],
  requiredLanguages: parseDelimitedString(posting.requiredLanguages),
  spokenLanguages: posting.spokenLanguages ?? [],
  cookingSkills: posting.cookingSkills ?? [],
  workType: posting.workType ?? 'FULL_TIME',
  otherSkills: posting.otherSkills ?? [],
  salaryType: posting.salaryType ?? 'FIXED',
  salaryCurrency: posting.salaryCurrency ?? 'HKD',
  fixedSalary: posting.fixedSalary ?? undefined,
  minSalary: posting.minSalary ?? undefined,
  maxSalary: posting.maxSalary ?? undefined,
  salaryOtherOption: posting.salaryOtherOption ?? '',
  minAge: posting.minAge ?? 25,
  maxAge: posting.maxAge ?? 45,
  minExperienceYears: posting.minExperienceYears ?? 0,
  maxExperienceYears: posting.maxExperienceYears ?? 40,
  accommodation: posting.accommodation ?? 'Live-in',
  employerNationality: posting.employerNationality ?? '',
  specialRequirements: posting.specialRequirements ?? '',
  preferredGender: posting.preferredGender ?? 'ANY',
  preferredNationality: posting.preferredNationality ?? 'ANY',
  restDayOption: posting.restDayOption ?? 'SUNDAY',
  requiredEducationLevel: posting.requiredEducationLevel ?? 'HIGH_SCHOOL',
  preferredMaritalStatus: posting.preferredMaritalStatus ?? 'ANY',
  preferredHasChildren: posting.preferredHasChildren ?? 'ANY',
  startDateFlexible: !posting.requiredStartDate,
  requiredStartDate: posting.requiredStartDate ? posting.requiredStartDate.replace(/-/g, '/') : '',
  benefits: posting.benefits ?? [],
  familyMembers: {
    adults: posting.familyMembers?.adults ?? 0,
    newborn: posting.familyMembers?.newborn ?? 0,
    infant: posting.familyMembers?.infant ?? posting.familyMembers?.children0to3 ?? 0,
    children: posting.familyMembers?.children ?? posting.familyMembers?.children3to12 ?? 0,
    teen: posting.familyMembers?.teen ?? posting.familyMembers?.children12to18 ?? 0,
    elderly: posting.familyMembers?.elderly ?? 0,
    pets: posting.familyMembers?.pets ?? 0,
    specialNeeds: posting.familyMembers?.specialNeeds ?? '',
  },
  livingArrangement: getValueFromOptions(posting.livingArrangement, livingArrangementOptions),
  numberOfRooms: posting.numberOfRooms ?? undefined,
  flatSize: posting.flatSize ?? undefined,
  preferredReligion: posting.preferredReligion ?? 'ANY',
  helperOriginPreference: posting.helperOriginPreference ?? 'ANY',
  contractSituation: posting.contractSituation ?? 'ABOUT_TO_FINISH',
});

const getSectionCompletion = (section: ActiveSection, values: JobPostingFormValues) => {
  switch (section) {
    case 'headline':
      return values.headline?.trim() ? 100 : 0;
    case 'overview': {
      const total = 7;
      const completed = [
        values.title,
        values.location,
        values.workType,
        values.description,
        values.salaryType,
        values.fixedSalary ? 'yes' : '',
        values.benefits.length > 0 ? 'yes' : '',
      ].filter(Boolean).length;
      return Math.round((completed / total) * 100);
    }
    case 'household': {
      const total = 6;
      const completed = [
        values.location,
        values.employerNationality,
        values.livingArrangement,
        values.requiredStartDate,
        values.restDayOption,
        Object.values(values.familyMembers).some((value: any) => value > 0) ? 'yes' : '',
      ].filter(Boolean).length;
      return Math.round((completed / total) * 100);
    }
    case 'requirements': {
      const total = 5;
      const completed = [
        values.requiredSkills.length > 0 ? 'yes' : '',
        values.cookingSkills.length > 0 ? 'yes' : '',
        values.otherSkills.length > 0 ? 'yes' : '',
        values.requiredLanguages.length > 0 ? 'yes' : '',
        values.spokenLanguages.length > 0 ? 'yes' : '',
      ].filter(Boolean).length;
      return Math.round((completed / total) * 100);
    }
    case 'preferences': {
      const total = 6;
      const completed = [
        values.contractSituation,
        values.requiredEducationLevel,
        values.preferredGender,
        values.preferredNationality,
        values.preferredReligion,
        values.specialRequirements,
      ].filter(Boolean).length;
      return Math.round((completed / total) * 100);
    }
    default:
      return 0;
  }
};

const FieldError: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) {
    return null;
  }

  return <p className="mt-2 text-xs font-medium text-destructive">{message}</p>;
};

const FormLabel: React.FC<{ children: React.ReactNode; hint?: string; required?: boolean }> = ({ children, hint, required }) => (
  <Label className="mb-2 block text-sm font-semibold text-foreground">
    {children}
    {required ? <span className="ml-0.5 text-red-600">*</span> : null}
    {hint ? <span className="ml-2 font-normal text-muted-foreground">{hint}</span> : null}
  </Label>
);

const SectionHeading: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  completion: number;
  required?: boolean;
  className?: string;
}> = ({ icon, title, description, completion, required, className }) => {
  const { t } = useTranslation();
  return (
  <div className={cn('mb-6 flex flex-col gap-3 border-b border-border pb-6 md:flex-row md:items-center md:justify-between', className)}>
    <div className="flex items-start gap-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-sm">{icon}</div>
      <div>
        <h2 className="font-serif text-2xl tracking-tight text-foreground">
          {title}
          {required ? <span className="ml-1 text-red-600">*</span> : null}
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
    </div>
    <Badge className="w-fit rounded-full bg-primary/10 px-3 py-1 text-primary hover:bg-primary/10">{completion}% {t('jobPosting.completed', '完成')}</Badge>
  </div>
  );
};

const PillButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'rounded-full border px-4 py-2 text-sm font-medium transition duration-200',
      active
        ? 'border-primary bg-primary text-primary-foreground shadow-md'
        : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-primary',
    )}
  >
    {children}
  </button>
);

const PreviewChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-full border border-border bg-primary/5 px-3 py-1 text-sm font-medium text-primary">{children}</span>
);

const SummaryItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0 rounded-2xl border border-border bg-muted p-4">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground break-words">{label}</p>
    <div className="mt-2 text-sm font-medium text-foreground break-words">{value}</div>
  </div>
);

const JobPostingFormPage: React.FC = () => {
  const { t } = useTranslation();
  // 與模組層 factory 同名，語言切換時自動重建（下方 JSX 無需改動）
  const SECTION_ITEMS = React.useMemo(() => getSectionItems(t), [t]);
  const SALARY_TYPE_OPTIONS = React.useMemo(() => getSalaryTypeOptions(t), [t]);
  const SALARY_CHOICE_OPTIONS = React.useMemo(() => getSalaryChoiceOptions(t), [t]);
  const SALARY_CURRENCY_OPTIONS = React.useMemo(() => getSalaryCurrencyOptions(t), [t]);
  const SALARY_OTHER_OPTIONS = React.useMemo(() => getSalaryOtherOptions(t), [t]);
  const PREFERENCE_ANY_OPTIONS = React.useMemo(() => getPreferenceAnyOptions(t), [t]);
  const FAMILY_MEMBERS = React.useMemo(() => getFamilyMembers(t), [t]);
  const originPreferenceOptions = React.useMemo((): Option[] => [
    { value: 'ANY', label: t('jobForm.any', '不限') },
    { value: 'LOCAL_PREFERRED', label: t('jobForm.originLocalPreferred', '本地完約優先') },
    { value: 'OVERSEAS_PREFERRED', label: t('jobForm.originOverseasNewPreferred', '海外新人優先') },
    { value: 'OVERSEAS_HELPER_PREFERRED', label: t('jobForm.originOverseasHelperPreferred', '海外外傭優先') },
  ], [t]);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { showAlert } = useAlert();
  const isEditMode = Boolean(id);

  const [pageLoading, setPageLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [activeSection, setActiveSection] = useState<ActiveSection>('overview');
  const [showPreview, setShowPreview] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<{ values: JobPostingSubmitValues; status: 'draft' | 'published' } | null>(null);
  const bypassLimitRef = useRef(false);
  const lastSaveSucceeded = useRef(false);
  const [legalMinimumWage, setLegalMinimumWage] = useState<{ amount: number; currency: string; effectiveDate: string } | null>(null);

  useEffect(() => {
    apiService.jobPostings.getLegalMinimumWage()
      .then((res) => setLegalMinimumWage(res?.data ?? null))
      .catch(() => setLegalMinimumWage(null));
  }, []);

  const { dictionariesByParent } = useDataDictionaries({
    parentCodes: [
      DATA_DICTIONARY_TYPES.NATIONALITY,
      DATA_DICTIONARY_TYPES.COOKING_SKILLS,
      DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE,
      DATA_DICTIONARY_TYPES.HOUSEWORK,
      DATA_DICTIONARY_TYPES.RELIGION,
      DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS,
      DATA_DICTIONARY_TYPES.BENEFITS,
      DATA_DICTIONARY_TYPES.OTHER_SKILLS,
      DATA_DICTIONARY_TYPES.JOB_LOCATION,
      DATA_DICTIONARY_TYPES.WORK_TYPE,
      DATA_DICTIONARY_TYPES.LIVING_ARRANGEMENT,
      DATA_DICTIONARY_TYPES.EDUCATION_LEVEL,
      DATA_DICTIONARY_TYPES.CONTRACT_SITUATION,
      DATA_DICTIONARY_TYPES.MARITAL_STATUS,
      DATA_DICTIONARY_TYPES.GENDER,
      DATA_DICTIONARY_TYPES.JOB_TITLE,
    ],
  });

  const dictOpts = useMemo(() => {
    const get = (type: string, mode?: 'english') => getOptionsFromDictionaries(dictionariesByParent.get(type), mode);
    return {
      jobLocation:       get(DATA_DICTIONARY_TYPES.JOB_LOCATION, 'english'),
      jobTitle:          get(DATA_DICTIONARY_TYPES.JOB_TITLE),
      workType:          get(DATA_DICTIONARY_TYPES.WORK_TYPE),
      livingArrangement: get(DATA_DICTIONARY_TYPES.LIVING_ARRANGEMENT, 'english'),
      education:         get(DATA_DICTIONARY_TYPES.EDUCATION_LEVEL),
      contractSituation: get(DATA_DICTIONARY_TYPES.CONTRACT_SITUATION),
      nationality:       [PREFERENCE_ANY_OPTIONS.nationality,   ...get(DATA_DICTIONARY_TYPES.NATIONALITY)],
      religion:          [PREFERENCE_ANY_OPTIONS.religion,      ...get(DATA_DICTIONARY_TYPES.RELIGION)],
      maritalStatus:     [PREFERENCE_ANY_OPTIONS.maritalStatus, ...get(DATA_DICTIONARY_TYPES.MARITAL_STATUS)],
      gender:            [PREFERENCE_ANY_OPTIONS.gender,        ...get(DATA_DICTIONARY_TYPES.GENDER)],
      careSkill:         get(DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE),
      housework:         get(DATA_DICTIONARY_TYPES.HOUSEWORK),
      cookingSkill:      get(DATA_DICTIONARY_TYPES.COOKING_SKILLS),
      otherSkill:        get(DATA_DICTIONARY_TYPES.OTHER_SKILLS),
      benefits:          get(DATA_DICTIONARY_TYPES.BENEFITS, 'english'),
      languages:         get(DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS, 'english'),
    };
  }, [dictionariesByParent, i18n.language]);

  const employerNationalityOptions = useMemo(
    () => EMPLOYER_COUNTRIES.map(c => ({ value: c.value, label: i18n.language === 'zh-TW' ? c.zh : c.en })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language],
  );

  const defaultValues = useMemo(() => createDefaultValues(user?.id), [user?.id]);

  const {
    control,
    register,
    reset,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<JobPostingFormValues, unknown, JobPostingSubmitValues>({
    resolver: zodResolver(getJobPostingSchema(t)),
    defaultValues,
  });

  const watchedValues = watch();

  const sectionCompletion = useMemo(
    () => ({
      headline: getSectionCompletion('headline', watchedValues),
      overview: getSectionCompletion('overview', watchedValues),
      household: getSectionCompletion('household', watchedValues),
      requirements: getSectionCompletion('requirements', watchedValues),
      preferences: getSectionCompletion('preferences', watchedValues),
    }),
    [watchedValues],
  );

  useEffect(() => {
    if (!isEditMode && user?.id) {
      setValue('employerId', user.id);
    }
  }, [isEditMode, setValue, user?.id]);

  // 貨幣跟工作地點（地區）綁定；目前只開放香港，先鎖定 HKD，其他幣種待開放更多地區後再啟用 LOCATION_CURRENCY_MAP
  useEffect(() => {
    if (watchedValues.salaryCurrency !== 'HKD') {
      setValue('salaryCurrency', 'HKD', { shouldDirty: true });
    }
  }, [watchedValues.location]);

  // 薪資類型／其他選項 合併下拉的衍生值與切換邏輯
  const salaryChoiceValue = watchedValues.salaryType === 'OTHER'
    ? `OTHER_${watchedValues.salaryOtherOption}`
    : watchedValues.salaryType;

  const handleSalaryChoiceChange = (value: string) => {
    if (value.startsWith('OTHER_')) {
      setValue('salaryType', 'OTHER', { shouldDirty: true, shouldValidate: true });
      setValue('salaryOtherOption', value.replace('OTHER_', ''), { shouldDirty: true, shouldValidate: true });
    } else {
      setValue('salaryType', value, { shouldDirty: true, shouldValidate: true });
      setValue('salaryOtherOption', '', { shouldDirty: true, shouldValidate: true });
    }
  };

  // 選擇「法定最低工資」時自動帶入後台設定的現行金額，不需人手輸入
  useEffect(() => {
    if (watchedValues.salaryType === 'OTHER' && watchedValues.salaryOtherOption === 'LEGAL_MINIMUM' && legalMinimumWage) {
      setValue('fixedSalary', legalMinimumWage.amount, { shouldDirty: true });
      setValue('salaryCurrency', legalMinimumWage.currency, { shouldDirty: true });
    }
  }, [watchedValues.salaryType, watchedValues.salaryOtherOption, legalMinimumWage]);

  // 選擇「範圍」時，預設最低薪金為法定最低工資
  useEffect(() => {
    if (watchedValues.salaryType === 'RANGE' && legalMinimumWage) {
      const current = Number(watchedValues.minSalary ?? 0);
      if (!current || current < legalMinimumWage.amount) {
        setValue('minSalary', legalMinimumWage.amount, { shouldDirty: true });
      }
    }
  }, [watchedValues.salaryType, legalMinimumWage]);

  useEffect(() => {
    const handlePageScroll = () => {
      const scrollPosition = window.scrollY + 220;

      for (let index = SECTION_ITEMS.length - 1; index >= 0; index -= 1) {
        const sectionId = SECTION_ITEMS[index].id;
        const element = document.getElementById(`section-${sectionId}`);

        if (element && scrollPosition >= element.offsetTop) {
          setActiveSection(sectionId);
          break;
        }
      }
    };

    window.addEventListener('scroll', handlePageScroll);
    handlePageScroll();

    return () => window.removeEventListener('scroll', handlePageScroll);
  }, []);

  useEffect(() => {
    if (!isEditMode || !id) {
      return;
    }

    const loadJobPosting = async () => {
      try {
        setPageLoading(true);
        setServerError('');
        const response = await apiService.jobPostings.getById(Number(id));

        if (response.success && response.data) {
          reset(mapPostingToFormValues(response.data, user?.id ?? 0, dictOpts.jobLocation, dictOpts.livingArrangement));
        }
      } catch (error) {
        console.error('Error loading job posting:', error);
        setServerError(t('jobForm.loadJobFailed', '載入職位資料失敗'));
      } finally {
        setPageLoading(false);
      }
    };

    void loadJobPosting();
  }, [id, isEditMode, dictOpts, reset, user?.id]);

  const scrollToSection = (section: ActiveSection) => {
    const element = document.getElementById(`section-${section}`);
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(section);
  };

  const toggleArrayValue = (
    field: 'requiredSkills' | 'requiredLanguages' | 'spokenLanguages' | 'cookingSkills' | 'otherSkills',
    value: string,
  ) => {
    const currentValues = getValues(field) as string[];
    const nextValues = currentValues.includes(value) ? currentValues.filter((item) => item !== value) : [...currentValues, value];
    setValue(field, nextValues as JobPostingFormValues[typeof field], { shouldDirty: true, shouldValidate: true });
  };

  const updateFamilyMember = (member: keyof JobPostingFormValues['familyMembers'], delta: number) => {
    const currentValues = getValues('familyMembers') as JobPostingFormValues['familyMembers'];
    const currentMemberValue = Number(currentValues?.[member] ?? 0);
    setValue(
      'familyMembers',
      {
        ...currentValues,
        [member]: Math.max(0, currentMemberValue + delta),
      },
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const getPreviewSalary = () => {
    const currencySymbol = watchedValues.salaryCurrency || 'HKD';

    if (watchedValues.salaryType === 'FIXED' && watchedValues.fixedSalary) {
      return `${currencySymbol} ${watchedValues.fixedSalary.toLocaleString()}`;
    }

    if (watchedValues.salaryType === 'RANGE' && watchedValues.minSalary && watchedValues.maxSalary) {
      return `${currencySymbol} ${watchedValues.minSalary.toLocaleString()} - ${watchedValues.maxSalary.toLocaleString()}`;
    }

    if (watchedValues.salaryType === 'OTHER' && watchedValues.salaryOtherOption) {
      return getLabelFromOptions(watchedValues.salaryOtherOption, SALARY_OTHER_OPTIONS);
    }

    return getLabelFromOptions(watchedValues.salaryType, SALARY_TYPE_OPTIONS);
  };

  const onSubmit = async (values: JobPostingSubmitValues, submitStatus: 'draft' | 'published') => {
    setServerError('');

    const submitData = {
      employerId: values.employerId,
      title: values.title.trim(),
      headline: values.headline?.trim() || undefined,
      description: values.description.trim(),
      location: values.location,
      requiredSkills: values.requiredSkills,
      requiredLanguages: toDelimitedString(values.requiredLanguages),
      spokenLanguages: values.spokenLanguages,
      cookingSkills: values.cookingSkills as CookingSkill[],
      workType: values.workType as CreateJobPostingRequest['workType'],
      otherSkills: values.otherSkills,
      salaryType: values.salaryType as CreateJobPostingRequest['salaryType'],
      salaryCurrency: values.salaryCurrency,
      fixedSalary: values.salaryType === 'FIXED' ? values.fixedSalary : undefined,
      minSalary: values.salaryType === 'RANGE' ? values.minSalary : undefined,
      maxSalary: values.salaryType === 'RANGE' ? values.maxSalary : undefined,
      salaryOtherOption: values.salaryType === 'OTHER' ? (values.salaryOtherOption as CreateJobPostingRequest['salaryOtherOption']) : undefined,
      status: (submitStatus === 'published' ? 'ACTIVE' : 'DRAFT') as CreateJobPostingRequest['status'],
      minAge: values.minAge,
      maxAge: values.maxAge,
      minExperienceYears: values.minExperienceYears,
      maxExperienceYears: values.maxExperienceYears,
      accommodation: values.accommodation,
      employerNationality: values.employerNationality || undefined,
      specialRequirements: values.specialRequirements?.trim() || undefined,
      preferredGender: values.preferredGender as CreateJobPostingRequest['preferredGender'],
      preferredNationality: values.preferredNationality as CreateJobPostingRequest['preferredNationality'],
      restDayOption: values.restDayOption as CreateJobPostingRequest['restDayOption'],
      requiredEducationLevel: values.requiredEducationLevel as CreateJobPostingRequest['requiredEducationLevel'],
      preferredMaritalStatus: values.preferredMaritalStatus as CreateJobPostingRequest['preferredMaritalStatus'],
      preferredHasChildren: values.preferredHasChildren as CreateJobPostingRequest['preferredHasChildren'],
      requiredStartDate: (() => {
        const v = values.requiredStartDate?.trim();
        if (!v) return undefined;
        // YYYY/MM → YYYY-MM-01, YYYY/MM/DD → YYYY-MM-DD
        return v.replace(/\//g, '-').split('-').length === 2 ? v.replace(/\//g, '-') + '-01' : v.replace(/\//g, '-');
      })(),
      benefits: values.benefits,
      familyMembers: values.familyMembers,
      livingArrangement: values.livingArrangement,
      numberOfRooms: values.numberOfRooms,
      flatSize: values.flatSize,
      preferredReligion: values.preferredReligion as CreateJobPostingRequest['preferredReligion'],
      helperOriginPreference: values.helperOriginPreference as CreateJobPostingRequest['helperOriginPreference'],
      contractSituation: values.contractSituation as CreateJobPostingRequest['contractSituation'],
    } as CreateJobPostingRequest;

    try {
      // 新建時檢查當月限制（用戶確認後繞過）
      if (!isEditMode && !bypassLimitRef.current) {
        const myPostings = await apiService.jobPostings.getByEmployer(user!.id, 0, 100);
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const thisMonthCount = (myPostings.data?.content || []).filter(
          (p: any) => new Date(p.createdAt || p.postedDate) >= monthStart
        ).length;
        if (thisMonthCount >= 1) {
          setPendingSubmit({ values, status: submitStatus });
          setShowLimitDialog(true);
          return;
        }
      }
      bypassLimitRef.current = false;

      const response = isEditMode && id
        ? await apiService.jobPostings.update(Number(id), submitData)
        : await apiService.jobPostings.create(submitData);

      if (!response.success) {
        const message = response.message || t('jobForm.opFailed', '操作失敗');
        setServerError(message);
        showAlert({ type: 'error', message });
        return;
      }

      lastSaveSucceeded.current = true;
      showAlert({
        type: 'success',
        message: isEditMode ? t('jobForm.updateSuccess', '職位更新成功！') : t('jobForm.createSuccess', '職位建立成功！'),
      });
      navigate('/my-job-postings');
    } catch (error: any) {
      const message = error.response?.data?.message || t('jobForm.opFailedRetry', '操作失敗，請稍後再試');
      setServerError(message);
      showAlert({ type: 'error', message });
      const code = error.response?.data?.error;
      if (code === 'PHONE_NOT_VERIFIED' || code === 'DISPLAY_NAME_NOT_SET') {
        navigate('/settings?tab=account');
      }
    }
  };

  const submitDraft = () => {
    void handleSubmit((values) => onSubmit(values, 'draft'))();
  };

  const submitPublished = async () => {
    // 發佈前檢查薪金不可低於法定最低工資
    if (legalMinimumWage) {
      const vals = getValues();
      const minWage = legalMinimumWage.amount;
      const currency = legalMinimumWage.currency;
      const belowMin =
        (vals.salaryType === 'FIXED' && vals.fixedSalary != null && Number(vals.fixedSalary) < minWage) ||
        (vals.salaryType === 'RANGE' && vals.minSalary != null && Number(vals.minSalary) < minWage);
      if (belowMin) {
        showAlert({ type: 'error', message: t('jobForm.salaryBelowMinimum', { currency, amount: minWage.toLocaleString() }) });
        return;
      }
    }

    const notVerified = !user?.phoneVerified;
    const noUsername = !user?.username?.trim();
    if (notVerified || noUsername) {
      // 先儲存草稿，完成後才顯示警告（避免被 success alert 覆蓋）
      lastSaveSucceeded.current = false;
      await handleSubmit((values) => onSubmit(values, 'draft'))();
      if (!lastSaveSucceeded.current) return; // save failed or form invalid — don't show misleading toast
      const reasons: string[] = [];
      if (notVerified) reasons.push(t('jobForm.requirePhone', '驗證 WhatsApp 電話'));
      if (noUsername) reasons.push(t('jobForm.requireUsername', '填寫稱呼'));
      showAlert({
        type: 'warning',
        message: t('jobForm.savedAsDraftPendingVerification', '廣告已儲存為草稿。請先{{reasons}}，然後再發佈。', { reasons: reasons.join('及') }),
      });
      return;
    }
    void handleSubmit((values) => onSubmit(values, 'published'))();
  };

  if (authLoading || pageLoading) {
    return (
      <div className="py-16">
        <div className="mx-auto flex max-w-6xl items-center justify-center rounded-[32px] border border-border bg-card p-16 shadow-md backdrop-blur">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Sparkles className="h-6 w-6" />
            </div>
            <p className="font-serif text-2xl text-foreground">{t('jobPosting.loading', 'Loading job posting form...')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    {/* 每月超限彈窗 */}
    <Dialog open={showLimitDialog} onOpenChange={setShowLimitDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('jobForm.monthlyLimitTitle', '每月招聘限制')}</DialogTitle>
          <DialogDescription>
            {t('jobForm.monthlyLimitBody1', '您本月已建立了 1 個招聘，是否需要再聘請多一名外傭？')}<br />
            {t('jobForm.monthlyLimitBody2', '如否，建議您修改現有招聘帖子。')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => { setShowLimitDialog(false); navigate('/my-job-postings'); }}>
            {t('jobForm.noModifyExisting', '否，去修改現有帖子')}
          </Button>
          <Button onClick={async () => {
            setShowLimitDialog(false);
            if (!pendingSubmit) return;
            const { values, status } = pendingSubmit;
            setPendingSubmit(null);
            bypassLimitRef.current = true;
            await onSubmit(values, status);
          }}>
            {t('jobForm.yesContinue', '是，繼續建立')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{isEditMode ? t('jobPosting.editJob', '編輯職位') : t('jobPosting.postNewJob', '發布新職位')}</h1>
          <p className="text-muted-foreground">{t('jobPosting.subtitle', '填寫您的招聘需求')}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button type="button" variant="outline" onClick={() => isEditMode ? navigate(-1) : navigate('/my-job-postings')}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={() => setShowPreview(true)}>
            <Eye className="h-4 w-4 mr-2" />
            {t('common.preview')}
          </Button>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <Card className="overflow-hidden rounded-[28px] border-border bg-card shadow-md backdrop-blur">
              <CardHeader className="bg-card">
                <CardTitle className="font-serif text-2xl text-foreground">{t('helperProfileForm.navigation', '導覽')}</CardTitle>
                <CardDescription>{t('jobForm.navDesc', '可快速跳轉各區塊，並即時顯示完成度。請以清晰、具體的家庭簡介撰寫內容。')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 p-4">
                <nav className="space-y-2">
                  {SECTION_ITEMS.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition',
                        activeSection === section.id
                          ? 'border-primary bg-primary text-primary-foreground shadow-lg'
                          : 'border-border bg-card text-foreground hover:border-primary/40 hover:text-primary',
                      )}
                    >
                      <div>
                        <p className="text-sm font-semibold">{t(`jobPosting.sections.${section.id}.title`, section.label)}</p>
                        <p className={cn('mt-1 text-xs', activeSection === section.id ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                          {t(`jobPosting.sections.${section.id}.desc`, section.description)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={cn('rounded-full px-2.5 py-0.5 text-[11px]', activeSection === section.id ? 'bg-card text-foreground' : 'bg-primary/10 text-primary')}>
                          {sectionCompletion[section.id]}%
                        </Badge>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </aside>

          <main>
            <form onSubmit={handleSubmit((values) => onSubmit(values, 'draft'))} className="space-y-8">
              {serverError ? <div className="break-all overflow-hidden rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{serverError}</div> : null}

              <Card id="section-headline" className="rounded-[32px] border-border bg-card shadow-md">
                <CardHeader className="pb-0">
                  <SectionHeading
                    icon={<Sparkles className="h-5 w-5" />}
                    title={t('jobForm.headlineTitle', '標題')}
                    description={t('jobForm.headlineDesc', '為招聘廣告訂立一個吸引且清晰的標題，讓求職者一眼就能掌握這份工作的重點。')}
                    completion={sectionCompletion.headline}
                    required
                    className="mb-0 pb-4"
                  />
                </CardHeader>
                <CardContent>
                  <Input
                    {...register('headline')}
                    placeholder={t('jobForm.headlinePlaceholder', '招聘廣告標題')}
                    className="h-11 rounded-xl"
                  />
                  <FieldError message={errors.headline?.message} />
                </CardContent>
              </Card>

              <Card id="section-overview" className="rounded-[32px] border-border bg-card shadow-md">
                <CardHeader className="pb-0">
                    <SectionHeading
                      icon={<Briefcase className="h-5 w-5" />}
                      title={t('jobPostingForm.jobOverview', 'Job overview')}
                      description={t('jobPostingForm.jobOverviewDesc', 'Set the role framing, salary posture, and the story of the position before candidates dive into the practical details.')}
                      completion={sectionCompletion.overview}
                      className="mb-0"
                    />
                </CardHeader>
                <CardContent className="space-y-8">
                  <div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <FormLabel required>{t('jobForm.position', '職位')}</FormLabel>
                        <Controller
                          control={control}
                          name="title"
                          render={({ field }) => (
                            <Select value={field.value || undefined} onValueChange={field.onChange}>
                              <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                                <SelectValue placeholder={t('jobForm.selectPosition', '請選擇職位')} />
                              </SelectTrigger>
                              <SelectContent>
                                {dictOpts.jobTitle.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <FieldError message={errors.title?.message} />
                      </div>

                      <div>
                        <FormLabel required>{t('jobForm.workTypeLabel', '聘用形式')}</FormLabel>
                        <Controller
                          control={control}
                          name="workType"
                          render={({ field }) => (
                            <Select value={field.value || undefined} onValueChange={field.onChange}>
                              <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                                <SelectValue placeholder={t('jobPostingForm.selectWorkType', 'Select work type')} />
                              </SelectTrigger>
                              <SelectContent>
                                {dictOpts.workType.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <FieldError message={errors.workType?.message} />
                      </div>

                      <div>
                        <FormLabel required>{t('jobPosting.fields.dayOff', 'Day-off')}</FormLabel>
                        <Controller
                          control={control}
                          name="restDayOption"
                          render={({ field }) => (
                            <Select value={field.value || undefined} onValueChange={field.onChange}>
                              <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                                <SelectValue placeholder={t('jobPostingForm.selectDayOff', 'Select day off')} />
                              </SelectTrigger>
                              <SelectContent>
                                {getRestDayOptions(t).map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>

                      <div>
                        <FormLabel required>{t('jobForm.expectedStartDate', '期望上班日期')}</FormLabel>
                        <div className="flex items-center gap-2">
                          <Input
                            type="text"
                            placeholder={t('jobForm.dateFormatPlaceholder', 'YYYY/MM/DD（DD 可省略）')}
                            maxLength={10}
                            disabled={!!watchedValues.startDateFlexible}
                            {...register('requiredStartDate')}
                            className="h-11 flex-1 rounded-xl"
                          />
                          <div className="flex shrink-0 items-center gap-1.5">
                            <Checkbox
                              id="startDateFlexible"
                              checked={!!watchedValues.startDateFlexible}
                              onCheckedChange={(checked) => {
                                setValue('startDateFlexible', !!checked, { shouldDirty: true, shouldValidate: true });
                                if (checked) setValue('requiredStartDate', '');
                              }}
                            />
                            <label htmlFor="startDateFlexible" className="cursor-pointer text-sm text-muted-foreground">{t('jobForm.anytime', '隨時')}</label>
                          </div>
                        </div>
                        {errors.requiredStartDate && (
                          <p className="text-xs text-destructive mt-1">{errors.requiredStartDate.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <FormLabel required>{t('jobForm.descriptionLabel', '描述')}</FormLabel>
                    <Textarea
                      {...register('description')}
                      rows={8}
                      className="min-h-[220px] rounded-[24px] border-border px-4 py-4"
                      placeholder={t('jobForm.descPlaceholder', '描述家庭優先事項、職責、日常作息，以及合適人選應具備的特質。')}
                    />
                    <FieldError message={errors.description?.message} />
                  </div>

                  <div className="grid gap-5 lg:grid-cols-2">
                    <div className="rounded-[28px] border border-border bg-card p-6">
                      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary/70">{t('jobPosting.compensation', 'Compensation')}<span className="ml-0.5 text-red-600">*</span></p>
                          <p className="text-sm text-muted-foreground">{t('jobPosting.compensationDesc', 'Keep the package explicit so the preview feels credible at first glance.')}</p>
                        </div>
                      </div>

                      <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                          <FormLabel>{t('jobPosting.fields.salaryType', 'Salary type')}</FormLabel>
                          <Select value={salaryChoiceValue || undefined} onValueChange={handleSalaryChoiceChange}>
                            <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                              <SelectValue placeholder={t('jobForm.selectSalaryType', '請選擇薪資類型')} />
                            </SelectTrigger>
                            <SelectContent>
                              {SALARY_CHOICE_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FieldError message={errors.salaryOtherOption?.message} />
                        </div>

                        <div>
                          <FormLabel>{t('jobPosting.fields.currency', 'Currency')}</FormLabel>
                          <Controller
                            control={control}
                            name="salaryCurrency"
                            render={({ field }) => (
                              <Select value={field.value || undefined} onValueChange={field.onChange} disabled>
                                <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                                  <SelectValue placeholder={t('jobForm.selectCurrency', '請選擇貨幣')} />
                                </SelectTrigger>
                                <SelectContent>
                                  {SALARY_CURRENCY_OPTIONS.filter((option) => option.value === 'HKD').map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>

                        {watchedValues.salaryType === 'RANGE' ? (
                          <>
                            <div>
                              <FormLabel required>{t('jobForm.minSalary', '最低薪金')}</FormLabel>
                              <Input
                                type="number"
                                className="h-11 rounded-xl"
                                min={legalMinimumWage?.amount}
                                placeholder=""
                                {...register('minSalary', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })}
                                onBlur={(e) => {
                                  const val = Number(e.target.value);
                                  if (legalMinimumWage && val > 0 && val < legalMinimumWage.amount) {
                                    setValue('minSalary', legalMinimumWage.amount, { shouldDirty: true });
                                    showAlert({ type: 'info', message: t('jobForm.salaryAutoAdjusted', `薪金已自動調整至法定最低工資 ${legalMinimumWage.currency} ${legalMinimumWage.amount.toLocaleString()}`) });
                                  }
                                }}
                              />
                            </div>
                            <div>
                              <FormLabel required>{t('jobForm.maxSalary', '最高薪金')}</FormLabel>
                              <Input
                                type="number"
                                className="h-11 rounded-xl"
                                placeholder=""
                                {...register('maxSalary', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })}
                                onBlur={(e) => {
                                  const val = Number(e.target.value);
                                  const min = (getValues('minSalary') as number) || 0;
                                  if (val > 0 && val <= min) {
                                    setValue('maxSalary', min + 1, { shouldDirty: true });
                                    showAlert({ type: 'info', message: t('jobForm.maxSalaryAdjusted', '最高薪金須大於最低薪金') });
                                  }
                                }}
                              />
                              <FieldError message={errors.maxSalary?.message} />
                            </div>
                            {legalMinimumWage && (
                              <p className="sm:col-span-2 text-xs text-muted-foreground">
                                {t('jobForm.legalMinHint', { currency: legalMinimumWage.currency, amount: legalMinimumWage.amount.toLocaleString() })}
                              </p>
                            )}
                          </>
                        ) : (
                          <div className="sm:col-span-2">
                            <FormLabel>{t('jobForm.salaryLabel', '薪金')}</FormLabel>
                            <Input
                              type="number"
                              className="h-11 rounded-xl"
                              min={legalMinimumWage?.amount}
                              placeholder={String(legalMinimumWage?.amount ?? 5100)}
                              {...register('fixedSalary', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })}
                              disabled={watchedValues.salaryType !== 'FIXED'}
                              onBlur={(e) => {
                                const val = Number(e.target.value);
                                if (legalMinimumWage && val > 0 && val < legalMinimumWage.amount) {
                                  setValue('fixedSalary', legalMinimumWage.amount, { shouldDirty: true });
                                  showAlert({ type: 'info', message: t('jobForm.salaryAutoAdjusted', `薪金已自動調整至法定最低工資 ${legalMinimumWage.currency} ${legalMinimumWage.amount.toLocaleString()}`) });
                                }
                              }}
                            />
                            {watchedValues.salaryType === 'FIXED' && legalMinimumWage && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {t('jobForm.legalMinHint', { currency: legalMinimumWage.currency, amount: legalMinimumWage.amount.toLocaleString() })}
                              </p>
                            )}
                            {watchedValues.salaryType === 'OTHER' && watchedValues.salaryOtherOption === 'LEGAL_MINIMUM' && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {legalMinimumWage
                                  ? t('jobForm.legalMinAutoFilled', { date: legalMinimumWage.effectiveDate })
                                  : t('jobForm.legalMinNotSet', '尚未在後台設定法定最低工資，請聯絡管理員。')}
                              </p>
                            )}
                            <FieldError message={errors.fixedSalary?.message} />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-[28px] border border-border bg-muted p-6">
                      <div className="mb-4 flex items-center gap-3">
                        <HeartHandshake className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-semibold text-foreground">{t('common.benefits')}</p>
                          <p className="text-sm text-muted-foreground">{t('jobPosting.fields.benefitsDesc', 'Structured as a checkbox grid for clean scanning.')}</p>
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {dictOpts.benefits.map((option) => {
                          const isChecked = watchedValues.benefits.includes(option.value);
                          return (
                            <label
                              key={option.value}
                              className={cn(
                                'flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition',
                                isChecked ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/30',
                              )}
                            >
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={() => {
                                  const current = getValues('benefits');
                                  const next = current.includes(option.value)
                                    ? current.filter((item) => item !== option.value)
                                    : [...current, option.value];
                                  setValue('benefits', next, { shouldDirty: true, shouldValidate: true });
                                }}
                              />
                              <span className="min-w-0 break-words text-sm font-medium text-foreground">{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card id="section-household" className="rounded-[32px] border-border bg-card shadow-md">
                <CardHeader className="pb-0">
                  <SectionHeading
                    icon={<Users className="h-5 w-5" />}
                      title={t('jobPostingForm.householdSetup', 'Household setup')}
                      description={t('jobPostingForm.householdSetupDesc', 'Map the household composition, accommodation rhythm, and logistics candidates need to assess fit quickly.')}
                    completion={sectionCompletion.household}
                    className="mb-0"
                  />
                </CardHeader>
                <CardContent className="space-y-8">
                  <div className="space-y-1.5">
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <FormLabel required>{t('jobForm.employerNationality', '僱主國籍')}</FormLabel>
                      <Controller
                        control={control}
                        name="employerNationality"
                        render={({ field }) => (
                          <Select value={field.value || undefined} onValueChange={field.onChange}>
                            <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                              <SelectValue placeholder={t('jobForm.selectNationality', '選擇國籍')} />
                            </SelectTrigger>
                            <SelectContent>
                              {employerNationalityOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError message={errors.employerNationality?.message} />
                    </div>

                    <div>
                      <FormLabel required>{t('jobPosting.fields.jobLocation', 'Job location')}</FormLabel>
                      <Controller
                        control={control}
                        name="location"
                        render={({ field }) => (
                          <Select value={field.value || undefined} onValueChange={field.onChange}>
                            <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                              <SelectValue placeholder={t('jobPostingForm.selectLocation', 'Select location')} />
                            </SelectTrigger>
                            <SelectContent>
                              {dictOpts.jobLocation.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError message={errors.location?.message} />
                    </div>

                    <div>
                      <FormLabel required>{t('jobPosting.fields.flatSize', 'Flat / house size')}</FormLabel>
                      <div className="relative">
                        <Input
                          type="number"
                          className="h-11 rounded-xl pr-10"
                          {...register('flatSize', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })}
                        />
                        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{t('jobForm.sqftUnit', '呎')}</span>
                      </div>
                      <FieldError message={errors.flatSize?.message} />
                    </div>

                    <div>
                      <FormLabel>{t('jobForm.numberOfRooms', '房間數量')}</FormLabel>
                      <Input
                        type="number"
                        className="h-11 rounded-xl"
                        min={0}
                        placeholder=""
                        {...register('numberOfRooms', { setValueAs: (value) => (value === '' ? undefined : Number(value)) })}
                      />
                    </div>

                    <div>
                      <FormLabel required>{t('jobPosting.fields.livingArrangement', 'Living arrangement')}</FormLabel>
                      <Controller
                        control={control}
                        name="livingArrangement"
                        render={({ field }) => (
                          <Select value={field.value || undefined} onValueChange={field.onChange}>
                            <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                              <SelectValue placeholder={t('jobPostingForm.selectArrangement', 'Select arrangement')} />
                            </SelectTrigger>
                            <SelectContent>
                              {dictOpts.livingArrangement.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError message={errors.livingArrangement?.message} />
                    </div>
                    {watchedValues.livingArrangement === 'Share room with kids' && (
                      <p className="text-xs font-medium text-red-600 self-center">
                        {t('jobForm.shareRoomWithKidsWarning', '注意：不可與10歲或以上異性兒童同房居住。')}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-red-600">{t('jobForm.liveInLaw', '根據香港法例，外傭必須居住於合約所列明的僱主住址。')}</p>
                  </div>

                  <div>
                    <FormLabel required>{t('common.familyMembers')}</FormLabel>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {FAMILY_MEMBERS.map((member) => (
                        <div key={member.key} className="rounded-[24px] border border-border bg-muted p-5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
                                <member.icon className="h-4 w-4 text-muted-foreground" />
                                {member.label}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 rounded-full border border-border bg-card p-1 shadow-sm">
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateFamilyMember(member.key, -1)}>
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="min-w-[2rem] text-center text-sm font-semibold text-foreground">{String(Number(watchedValues.familyMembers[member.key] ?? 0))}</span>
                              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateFamilyMember(member.key, 1)}>
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4">
                      <FormLabel>{t('jobForm.specialNeeds', '特別需要')}</FormLabel>
                      <Textarea
                        placeholder={t('jobForm.specialNeedsPlaceholder', '例如：長者需要輪椅照顧、嬰兒過敏需注意飲食')}
                        {...register('familyMembers.specialNeeds')}
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card id="section-preferences" className="rounded-[32px] border-border bg-card shadow-md">
                <CardHeader className="pb-0">
                    <SectionHeading
                      icon={<Globe2 className="h-5 w-5" />}
                      title={t('jobPostingForm.candidatePreferences', 'Candidate preferences')}
                      description={t('jobPostingForm.candidatePreferencesDesc', 'Define boundaries for profile fit - education, contract readiness, experience band, and household-specific notes.')}
                      completion={sectionCompletion.preferences}
                      className="mb-0"
                    />
                </CardHeader>
                <CardContent className="space-y-8">
                  <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                    {([
                       ['helperOriginPreference', t('jobForm.originPreference', '偏好類別'), originPreferenceOptions],
                       ['preferredGender', t('jobForm.helperGender', '外傭性別'), dictOpts.gender],
                       ['preferredNationality', t('common.nationality'), dictOpts.nationality],
                       ['preferredReligion', t('common.religion'), dictOpts.religion],
                       ['requiredEducationLevel', t('jobForm.educationLabel', '教育程度'), dictOpts.education],
                       ['preferredMaritalStatus', t('common.maritalStatus'), dictOpts.maritalStatus],
                       ['contractSituation', t('jobPostingForm.contractSituation', 'Contract situation'), dictOpts.contractSituation],
                    ] as Array<[FormFieldName, string, Option[]]>).map(([fieldName, label, options]) => (
                      <div key={fieldName} className="min-w-0">
                        <FormLabel required>{label}</FormLabel>
                        <Controller
                          control={control}
                          name={fieldName}
                          render={({ field }) => (
                            <Select value={field.value || undefined} onValueChange={field.onChange}>
                              <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                                <SelectValue placeholder={t('jobPostingForm.selectPlaceholder', { defaultValue: `Select ${label.toLowerCase()}` })} className="truncate" />
                              </SelectTrigger>
                              <SelectContent>
                                {options.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                        <FieldError message={errors[fieldName]?.message as string | undefined} />
                      </div>
                    ))}

                    <div>
                      <FormLabel>{t('jobForm.childrenLabel', '子女')}</FormLabel>
                      <Controller
                        control={control}
                        name="preferredHasChildren"
                        render={({ field }) => (
                          <Select value={field.value || undefined} onValueChange={field.onChange}>
                            <SelectTrigger className="h-11 rounded-xl overflow-hidden">
                              <SelectValue placeholder={t('jobForm.selectPlaceholder', '請選擇')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ANY">{t('jobForm.any', '不限')}</SelectItem>
                              <SelectItem value="HAS">{t('jobForm.has', '有')}</SelectItem>
                              <SelectItem value="NONE">{t('jobForm.none', '沒有')}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <FormLabel>
                        {t('jobPosting.fields.ageRange', '年齡範圍 Age range')}: {Number(watchedValues.minAge ?? 18)} - {Number(watchedValues.maxAge ?? 60)}
                      </FormLabel>
                      <Controller
                        control={control}
                        name="minAge"
                        render={({ field: minField }) => (
                          <Controller
                            control={control}
                            name="maxAge"
                            render={({ field: maxField }) => (
                              <Slider
                                min={18}
                                max={60}
                                step={1}
                                value={[Number(minField.value ?? 18), Number(maxField.value ?? 60)]}
                                onValueChange={(vals) => {
                                  minField.onChange(vals[0]);
                                  maxField.onChange(vals[1]);
                                }}
                                className="mt-3"
                              />
                            )}
                          />
                        )}
                      />
                      <FieldError message={errors.maxAge?.message} />
                    </div>
                    <div>
                      <FormLabel>
                        {t('jobForm.workExpRange', '工作經驗')}: {Number(watchedValues.minExperienceYears ?? 0)} - {Number(watchedValues.maxExperienceYears ?? 40)}
                      </FormLabel>
                      <Controller
                        control={control}
                        name="minExperienceYears"
                        render={({ field: minField }) => (
                          <Controller
                            control={control}
                            name="maxExperienceYears"
                            render={({ field: maxField }) => (
                              <Slider
                                min={0}
                                max={40}
                                step={1}
                                value={[Number(minField.value ?? 0), Number(maxField.value ?? 40)]}
                                onValueChange={(vals) => {
                                  minField.onChange(vals[0]);
                                  maxField.onChange(vals[1]);
                                }}
                                className="mt-3"
                              />
                            )}
                          />
                        )}
                      />
                      <FieldError message={errors.maxExperienceYears?.message} />
                    </div>
                  </div>

                </CardContent>
              </Card>

              <Card id="section-requirements" className="rounded-[32px] border-border bg-card shadow-md">
                <CardHeader className="pb-0">
                    <SectionHeading
                      icon={<Languages className="h-5 w-5" />}
                      title={t('jobForm.sectionRequirements', '語言與技能')}
                      description={t('jobPostingForm.skillsLanguageDesc', 'Layer care responsibilities, cooking capabilities, and both required + spoken languages without losing backend alignment.')}
                      completion={sectionCompletion.requirements}
                      className="mb-0"
                    />
                </CardHeader>
                <CardContent className="space-y-8">
                  <div>
                    <h3 className="mb-4 border-b border-border pb-2 text-base font-semibold text-foreground">
                      {t('jobForm.languageReq', '語言要求')}<span className="ml-0.5 text-red-600">*</span>
                    </h3>
                    <div className="rounded-[24px] border border-border bg-muted p-5">
                      <div className="flex flex-wrap gap-2.5">
                        {dictOpts.languages.map((option) => (
                          <PillButton key={option.value} active={watchedValues.requiredLanguages.includes(option.value)} onClick={() => toggleArrayValue('requiredLanguages', option.value)}>
                            {option.label}
                          </PillButton>
                        ))}
                      </div>
                      <FieldError message={errors.requiredLanguages?.message as string | undefined} />
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-4 border-b border-border pb-2 text-base font-semibold text-foreground">
                      {t('jobForm.duties', '職責')}<span className="ml-0.5 text-red-600">*</span>
                    </h3>
                    <div className="grid gap-6 lg:grid-cols-2">
                      <div className="rounded-[24px] border border-border bg-muted p-5">
                        <FormLabel>{t('jobForm.housework', '家務')}</FormLabel>
                        <div className="flex flex-wrap gap-2.5">
                          {dictOpts.housework.map((option) => (
                            <PillButton key={option.value} active={watchedValues.requiredSkills.includes(option.value)} onClick={() => toggleArrayValue('requiredSkills', option.value)}>
                              {option.label}
                            </PillButton>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-border bg-muted p-5">
                        <FormLabel>{t('jobForm.care', '照顧')}</FormLabel>
                        <div className="flex flex-wrap gap-2.5">
                          {dictOpts.careSkill.map((option) => (
                            <PillButton key={option.value} active={watchedValues.requiredSkills.includes(option.value)} onClick={() => toggleArrayValue('requiredSkills', option.value)}>
                              {option.label}
                            </PillButton>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-4 border-b border-border pb-2 text-base font-semibold text-foreground">{t('common.skills')}</h3>
                    <div className="space-y-6">
                      <div className="rounded-[24px] border border-border bg-muted p-5">
                        <FormLabel>{t('common.cookingSkills')}</FormLabel>
                        <div className="flex flex-wrap gap-2.5">
                          {dictOpts.cookingSkill.map((option) => (
                            <PillButton key={option.value} active={watchedValues.cookingSkills.includes(option.value)} onClick={() => toggleArrayValue('cookingSkills', option.value)}>
                              {option.label}
                            </PillButton>
                          ))}
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-border bg-muted p-5">
                        <FormLabel>{t('jobPosting.fields.additionalSkills', 'Additional skills')}</FormLabel>
                        <div className="flex flex-wrap gap-2.5">
                          {dictOpts.otherSkill.map((option) => (
                            <PillButton key={option.value} active={watchedValues.otherSkills.includes(option.value)} onClick={() => toggleArrayValue('otherSkills', option.value)}>
                              {option.label}
                            </PillButton>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <footer className="sticky bottom-4 z-20 rounded-[28px] border border-border bg-card p-4 shadow-md backdrop-blur">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-muted-foreground">
                    <p className="font-semibold text-foreground">{t('jobPosting.previewBeforePublish', 'Preview before publishing.')}</p>
                    <p>{t('jobPosting.saveNotice', 'Save draft keeps the posting private. Save & publish sends status ACTIVE to the backend.')}</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="button" variant="outline" className="rounded-full px-5" onClick={() => setShowPreview(true)}>
                      <Eye className="h-4 w-4" />
                      {t('common.preview')}
                    </Button>
                    <Button type="button" variant="outline" className="rounded-full px-5" disabled={isSubmitting} onClick={submitDraft}>
                      <Save className="h-4 w-4" />
                      {t('common.save')}
                    </Button>
                    <Button type="button" className="rounded-full px-5" disabled={isSubmitting} onClick={submitPublished}>
                      <Sparkles className="h-4 w-4" />
                      {t('jobPostingForm.savePublish', 'Save & publish')}
                    </Button>
                  </div>
                </div>
              </footer>
            </form>
          </main>
        </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-h-[90vh] w-[70vw] sm:max-w-[70vw] overflow-auto rounded-[32px] border-border bg-card p-0 shadow-md">
          <DialogHeader className="border-b border-border px-8 pb-6 pt-8 text-left">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <Badge className="mb-4 w-fit rounded-full bg-primary/10 px-3 py-1 text-primary hover:bg-primary/10">{t('common.preview', 'Preview')}</Badge>
                <DialogTitle className="break-words font-serif text-4xl tracking-tight text-foreground">
                  {watchedValues.headline || getLabelFromOptions(watchedValues.title, dictOpts.jobTitle)}
                </DialogTitle>
              </div>
              <div className="rounded-[24px] border border-border bg-muted px-5 py-4 text-foreground">
                <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">{t('jobPosting.preview.package', 'Package')}</p>
                <p className="mt-2 font-serif text-3xl">{getPreviewSalary()}</p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-8 px-8 py-8">
            <Card className="rounded-[28px] border-border shadow-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-serif text-2xl text-foreground"><Briefcase className="h-5 w-5 text-primary" /> {t('jobForm.sectionOverview', '職位概覽')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <SummaryItem label={t('jobForm.workTypeLabel', '聘用形式')} value={getLabelFromOptions(watchedValues.workType, dictOpts.workType)} />
                  <SummaryItem label={t('jobForm.position', '職位')} value={watchedValues.title} />
                  <SummaryItem label={t('jobPosting.fields.dayOff', 'Day-off')} value={getLabelFromOptions(watchedValues.restDayOption, getRestDayOptions(t))} />
                  <SummaryItem label={t('jobForm.expectedStartDate', '期望上班日期')} value={watchedValues.requiredStartDate || t('jobPostingForm.flexible', 'Flexible')} />
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t('jobForm.descriptionLabel', '描述')}</p>
                  <p className="whitespace-pre-wrap break-words text-sm leading-7 text-muted-foreground">{watchedValues.description || t('jobPosting.preview.noDescription', 'No description yet.')}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {watchedValues.benefits.length > 0
                    ? formatDelimitedText(watchedValues.benefits, dictOpts.benefits).map((benefit) => <PreviewChip key={benefit}>{benefit}</PreviewChip>)
                    : <PreviewChip>{t('jobPosting.preview.noBenefits', 'No benefits selected yet')}</PreviewChip>}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[28px] border-border shadow-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-serif text-2xl text-foreground"><Home className="h-5 w-5 text-primary" /> {t('jobForm.sectionHousehold', '家庭概況')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <SummaryItem label={t('jobPosting.fields.jobLocation', 'Job location')} value={getLabelFromOptions(watchedValues.location, dictOpts.jobLocation)} />
                  <SummaryItem label={t('jobPosting.fields.flatSize', 'Flat / house size')} value={watchedValues.flatSize ? `${watchedValues.flatSize} ${t('jobForm.sqftUnit', '呎')}` : t('jobPostingForm.notSpecified', 'Not specified')} />
                  <SummaryItem label={t('jobForm.livingArrangement', '住宿安排')} value={getLabelFromOptions(watchedValues.livingArrangement, dictOpts.livingArrangement)} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {FAMILY_MEMBERS.filter((member) => (watchedValues.familyMembers[member.key] as any) > 0).map((member) => (
                    <PreviewChip key={member.key}>
                      <span className="inline-flex items-center gap-1">
                        <member.icon className="h-3.5 w-3.5" />
                        {`${member.label}: ${String(Number(watchedValues.familyMembers[member.key] ?? 0))}`}
                      </span>
                    </PreviewChip>
                  ))}
                  {!FAMILY_MEMBERS.some((member) => (watchedValues.familyMembers[member.key] as any) > 0) ? <PreviewChip>{t('jobPostingForm.noFamilyCounters', 'No family counters selected yet')}</PreviewChip> : null}
                </div>
                <SummaryItem label={t('jobForm.specialNeeds', '特別需要')} value={watchedValues.familyMembers.specialNeeds || t('jobPostingForm.noneAdded', 'None added')} />
              </CardContent>
            </Card>

            <Card className="rounded-[28px] border-border shadow-none">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-foreground">{t('jobPostingForm.candidatePreferences', 'Candidate preferences')}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <SummaryItem label={t('jobForm.originPreference', '偏好類別')} value={getLabelFromOptions(watchedValues.helperOriginPreference, originPreferenceOptions)} />
                <SummaryItem label={t('jobForm.helperGender', '外傭性別')} value={getLabelFromOptions(watchedValues.preferredGender, dictOpts.gender)} />
                <SummaryItem label={t('common.nationality')} value={getLabelFromOptions(watchedValues.preferredNationality, dictOpts.nationality)} />
                <SummaryItem label={t('common.religion')} value={getLabelFromOptions(watchedValues.preferredReligion, dictOpts.religion)} />
                <SummaryItem label={t('jobForm.educationLabel', '教育程度')} value={getLabelFromOptions(watchedValues.requiredEducationLevel, dictOpts.education)} />
                <SummaryItem label={t('common.maritalStatus')} value={getLabelFromOptions(watchedValues.preferredMaritalStatus, dictOpts.maritalStatus)} />
                <SummaryItem label={t('jobPostingForm.contract', 'Contract')} value={getLabelFromOptions(watchedValues.contractSituation, dictOpts.contractSituation)} />
                <SummaryItem label={t('jobForm.childrenLabel', '子女')} value={watchedValues.preferredHasChildren === 'HAS' ? t('jobForm.has', '有') : watchedValues.preferredHasChildren === 'NONE' ? t('jobForm.none', '沒有') : t('jobForm.any', '不限')} />
                <SummaryItem label={t('jobPostingForm.ageRange', 'Age range')} value={`${watchedValues.minAge ?? '—'} - ${watchedValues.maxAge ?? '—'}`} />
                <SummaryItem label={t('jobForm.workExpRange', '工作經驗')} value={`${watchedValues.minExperienceYears ?? '—'} - ${watchedValues.maxExperienceYears ?? '—'} ${t('jobForm.yearsUnit', '年')}`} />
              </CardContent>
            </Card>

            <Card className="rounded-[28px] border-border shadow-none">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-serif text-2xl text-foreground"><Languages className="h-5 w-5 text-primary" /> {t('jobForm.sectionRequirements', '語言與技能')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t('jobForm.languageReq', '語言要求')}</p>
                  <div className="flex flex-wrap gap-2">
                    {watchedValues.requiredLanguages.length > 0
                      ? formatDelimitedText(watchedValues.requiredLanguages, dictOpts.languages).map((language) => <PreviewChip key={language}>{language}</PreviewChip>)
                      : <PreviewChip>{t('jobPosting.preview.noRequiredLanguages', 'No required languages selected')}</PreviewChip>}
                  </div>
                </div>
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t('jobForm.duties', '職責')}</p>
                  <div className="flex flex-wrap gap-2">
                    {watchedValues.requiredSkills.length > 0
                      ? watchedValues.requiredSkills.map((skill) => (
                          <PreviewChip key={skill}>{getLabelFromOptions(skill, [...dictOpts.careSkill, ...dictOpts.housework])}</PreviewChip>
                        ))
                      : <PreviewChip>{t('jobPosting.preview.noRequiredSkills', 'No required skills selected')}</PreviewChip>}
                  </div>
                </div>
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t('common.skills')}</p>
                  <div className="flex flex-wrap gap-2">
                    {watchedValues.cookingSkills.length > 0
                      ? watchedValues.cookingSkills.map((skill) => <PreviewChip key={skill}>{getLabelFromOptions(skill, dictOpts.cookingSkill)}</PreviewChip>)
                      : null}
                    {watchedValues.otherSkills.length > 0
                      ? watchedValues.otherSkills.map((skill) => <PreviewChip key={skill}>{getLabelFromOptions(skill, dictOpts.otherSkill)}</PreviewChip>)
                      : null}
                    {watchedValues.cookingSkills.length === 0 && watchedValues.otherSkills.length === 0
                      ? <PreviewChip>{t('jobPosting.preview.noCookingSkills', 'No cooking skills selected')}</PreviewChip>
                      : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter className="border-t border-border px-8 py-6">
              <Button type="button" variant="outline" className="rounded-full px-5" onClick={() => setShowPreview(false)}>
               {t('common.close')}
              </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default JobPostingFormPage;



